/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { getModelsForBySearchTermV2Async } from './DataActionExtension.g';
import { IAriModelResponseEntity } from './DataServiceEntities.g';
import { ICustomConfigurationExtension } from '../utils/CustomConfigurationExtension';
type pageType = 'Category' | 'Search';
/**
 * SearchModels Input Action
 */
export class SearchModelsInput implements Msdyn365.IActionInput {
    public searchText: string | undefined;
    public queryResultSettings: IQueryResultSettings;
    public pageType: pageType;
    public apiSettings: Msdyn365.ICommerceApiSettings;
    public isRefineEnabled?: boolean;
    public enableDirectLinkAndRedirect: boolean;
    constructor(
        searchText: string | undefined,
        queryResultSettings: IQueryResultSettings,
        pageType: pageType,
        apiSettings: Msdyn365.ICommerceApiSettings,
        isRefineEnabled?: boolean,
        enableDirectLinkAndRedirect: boolean = false
    ) {
        this.pageType = pageType;
        this.queryResultSettings = queryResultSettings;
        this.searchText = searchText;
        this.apiSettings = apiSettings;
        this.isRefineEnabled = isRefineEnabled;
        this.enableDirectLinkAndRedirect = enableDirectLinkAndRedirect;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => {
        const queryResultSettings = this.queryResultSettings.Paging ? this.queryResultSettings.Paging : null;
        if (queryResultSettings) {
            const cacheKey = buildCacheKey(
                `${this.pageType}-${this.searchText}${queryResultSettings.Top}-${queryResultSettings.Skip}`,
                this.apiSettings
            );
            return cacheKey;
        } else {
            return buildCacheKey(`${this.pageType}-${this.searchText}`, this.apiSettings);
        }
    };
    public getCacheObjectType = () => 'ariModel';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface ISearchModelsData {
    ariSearchResults: IAriModelResponseEntity;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createModelSearchInput = (
    args: Msdyn365.ICreateActionContext<{ itemsPerPage: number; expandResults: boolean; enableDirectLinkAndRedirect?: boolean }>
): Msdyn365.IActionInput => {
    let searchText = '';
    const enableDirectLinkAndRedirect = args?.config?.enableDirectLinkAndRedirect;
    if (enableDirectLinkAndRedirect) {
        const queries: string[] = [];
        for (const key in args.requestContext.query) {
            if (key.startsWith('q')) {
                queries.push(args.requestContext.query[key]);
            }
        }
        searchText = queries.join(';');
    } else {
        searchText = args.requestContext.query?.q || '';
    }

    const pageType =
        args.requestContext.urlTokens.pageType === 'Category' || (args.requestContext.query && args.requestContext.query.categoryId)
            ? 'Category'
            : 'Search';
    const queryResultSettings = QueryResultSettingsProxy.fromInputData(args).QueryResultSettings;
    if (queryResultSettings && queryResultSettings.Paging) {
        queryResultSettings.Paging.Top = args?.config?.itemsPerPage || 10;
    }

    // const isRefineEnabled = args.requestContext.query?.refine ? true : false;
    const isRefineEnabled = args?.config?.expandResults || false;

    return new SearchModelsInput(
        searchText,
        queryResultSettings,
        pageType,
        args.requestContext.apiSettings,
        isRefineEnabled,
        enableDirectLinkAndRedirect
    );
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */

async function action(input: SearchModelsInput, ctx: Msdyn365.IActionContext): Promise<ISearchModelsData> {
    if (input.pageType === 'Search') {
        const appConfig = ctx.requestContext.app.config as ICustomConfigurationExtension;
        const brandCodes = appConfig.websiteAriBrand?.length ? appConfig.websiteAriBrand.split(',') : [];

        const responses = await getModelsForBySearchTermV2Async(
            { callerContext: ctx, queryResultSettings: input.queryResultSettings },
            input.searchText!,
            brandCodes,
            input.isRefineEnabled!
        );

        if (responses && responses.length > 0) {
            return { ariSearchResults: responses[0] };
        }
    }

    return { ariSearchResults: { Models: [], TotalCount: 0 } };
}

export default Msdyn365.createObservableDataAction<ISearchModelsData>({
    action: <Msdyn365.IAction<ISearchModelsData>>action,
    id: 'SearchModels',
    input: createModelSearchInput
});
